import { useState, useContext } from "react";
import { UserContext } from "../context/UserContext";
import CurrencyDisplay from "../components/CurrencyDisplay";
import { shopifyCustomerRecover } from "../lib/shopify";

import styles from '../styles/login.module.css';
import { IoMdClose } from "react-icons/io";
import { PiUserCircleDashedThin } from "react-icons/pi";
import { CiCircleCheck, CiCircleRemove } from "react-icons/ci";

const Login = ({ openLogin, setOpenLogin }) => {
    const { user, login, logout, register, updateCustomer, updateAddress } = useContext(UserContext);

    // Login / Register / Forgot password view: 'login' | 'register' | 'forgot'
    const [authView, setAuthView] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [authError, setAuthError] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [resetSent, setResetSent] = useState(false);

    // Account form state
    const [formData, setFormData] = useState({
        address: '',
        email: '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [addressChange, setAddressChange] = useState(false);
    const [emailChange, setEmailChange] = useState(false);
    const [passwordMatch, setPasswordMatch] = useState(false);
    const [openAddress, setOpenAddress] = useState(false);
    const [openPassword, setOpenPassword] = useState(false);

    const totalSpent = (user?.orders ?? []).reduce((sum, o) => sum + (o.total ?? 0), 0);

    const switchView = (view) => {
        setAuthView(view);
        setAuthError('');
        setResetSent(false);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setAuthError('');
        setAuthLoading(true);
        try {
            await login(email.trim(), password.trim());
            setEmail('');
            setPassword('');
        } catch {
            setAuthError('Incorrect email or password');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setAuthError('');
        setAuthLoading(true);
        try {
            await register(firstName.trim(), lastName.trim(), email.trim(), password.trim());
            setEmail(''); setPassword(''); setFirstName(''); setLastName('');
        } catch (err) {
            setAuthError(err.message ?? 'Registration failed');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setAuthError('');
        setAuthLoading(true);
        try {
            await shopifyCustomerRecover(email.trim());
            setResetSent(true);
        } catch {
            setAuthError('Could not send reset email. Check the address and try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleLogout = async () => {
        await logout();
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'newPassword' || name === 'confirmPassword') {
                setPasswordMatch(updated.newPassword === updated.confirmPassword);
            }
            return updated;
        });
    };

    const handleAddressChange = async (e) => {
        e.preventDefault();
        try {
            await updateAddress(formData.address);
            setAddressChange(false);
            setFormData(prev => ({ ...prev, address: '' }));
        } catch {
            alert('Could not update address');
        }
    };

    const handleEmailChange = async (e) => {
        e.preventDefault();
        try {
            await updateCustomer({ email: formData.email });
            setEmailChange(false);
            setFormData(prev => ({ ...prev, email: '' }));
        } catch {
            alert('Could not update email');
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        const { newPassword, confirmPassword } = formData;
        if (!newPassword || !confirmPassword) return alert('Please fill out all password fields');
        if (newPassword !== confirmPassword) return alert('Passwords do not match');
        try {
            await updateCustomer({ password: newPassword });
            setFormData(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
            setOpenPassword(false);
        } catch {
            alert('Could not update password');
        }
    };

    return (
        <div className={openLogin ? styles.login : styles.loginHidden}>
            <div className={styles.headerLogin}>
                <div className={styles.accountTitle}>
                    <p>Your account</p>
                    {user && <p className={styles.logout} onClick={handleLogout}>Logout</p>}
                </div>
                <div className={styles.closeAccount}>
                    <IoMdClose onClick={() => setOpenLogin(false)} />
                </div>
            </div>

            <div className={styles.loginmain}>
                {user === null ? (
                    <div className={styles.loginForm}>
                        <p className={styles.loginFail}>{authError}</p>

                        {authView === 'login' && (
                            <>
                                <p>Login</p>
                                <form onSubmit={handleLogin}>
                                    <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" required />
                                    <input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="Password" required />
                                    <button type="submit" disabled={authLoading}>{authLoading ? 'Logging in...' : 'Login'}</button>
                                </form>
                                <p className={styles.toggleAuth} onClick={() => switchView('forgot')}>Forgot password?</p>
                                <p className={styles.toggleAuth} onClick={() => switchView('register')}>Don't have an account? Register</p>
                            </>
                        )}

                        {authView === 'register' && (
                            <>
                                <p>Create account</p>
                                <p className={styles.authNote}>Or activate your account from the confirmation email after your first order.</p>
                                <form onSubmit={handleRegister}>
                                    <input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" required />
                                    <input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" required />
                                    <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" required />
                                    <input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="Password" required />
                                    <button type="submit" disabled={authLoading}>{authLoading ? 'Creating...' : 'Create account'}</button>
                                </form>
                                <p className={styles.toggleAuth} onClick={() => switchView('login')}>Already have an account? Login</p>
                            </>
                        )}

                        {authView === 'forgot' && (
                            <>
                                <p>Reset password</p>
                                {resetSent ? (
                                    <p className={styles.authNote}>Check your email — we sent a reset link to <strong>{email}</strong>.</p>
                                ) : (
                                    <form onSubmit={handleForgotPassword}>
                                        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email" type="email" required />
                                        <button type="submit" disabled={authLoading}>{authLoading ? 'Sending...' : 'Send reset link'}</button>
                                    </form>
                                )}
                                <p className={styles.toggleAuth} onClick={() => switchView('login')}>Back to login</p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className={styles.account}>

                        {/* Profile header */}
                        <div className={styles.profileHeader}>
                            <div className={styles.dp}>
                                <PiUserCircleDashedThin />
                            </div>
                            <div className={styles.profileInfo}>
                                <p className={styles.profileName}>{user.first_name} {user.last_name}</p>
                                <p className={styles.profileEmail}>{user.email}</p>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className={styles.statsRow}>
                            <div className={styles.statBlock}>
                                <span className={styles.statVal}>{user.orders.length}</span>
                                <span className={styles.statLbl}>Orders</span>
                            </div>
                            <div className={styles.statDivider} />
                            <div className={styles.statBlock}>
                                <span className={styles.statVal}>
                                    <CurrencyDisplay amount={totalSpent} currency="CAD" />
                                </span>
                                <span className={styles.statLbl}>Total spent</span>
                            </div>
                        </div>

                        {/* Orders */}
                        <div className={styles.sectionLabel}>Recent orders</div>
                        <div className={styles.orders}>
                            <div className={styles.tableHeader}>
                                <span>#</span>
                                <span>Status</span>
                                <span>Items</span>
                                <span>Total</span>
                            </div>
                            {user.orders.length === 0 && (
                                <p className={styles.emptyOrders}>No orders yet</p>
                            )}
                            {user.orders.map(o => (
                                <div className={styles.orderRow} key={o.id}>
                                    <span>#{o.id}</span>
                                    <span className={styles.orderStatus}>{o.status?.toLowerCase()}</span>
                                    <span>{o.items.length}</span>
                                    <span><CurrencyDisplay amount={o.total} currency={o.currency} /></span>
                                </div>
                            ))}
                        </div>

                        {/* Accordions */}
                        <div className={styles.accordionMain}>
                            <div className={styles.accordionHeader} onClick={() => setOpenAddress(a => !a)}>
                                <span>Address book</span>
                                <span>{openAddress ? '−' : '+'}</span>
                            </div>
                            <div className={openAddress ? styles.accordionContentOpen : styles.accordionContentClosed}>
                                <div className={styles.accordionInner}>
                                    <p className={styles.currentValue}>{user.address || 'No address saved'}</p>
                                    <p className={styles.changeLink} onClick={() => setAddressChange(p => !p)}>
                                        {addressChange ? 'cancel' : 'change'}
                                    </p>
                                    {addressChange && (
                                        <form onSubmit={handleAddressChange} className={styles.inlineForm}>
                                            <input type="text" name="address" placeholder="New address" value={formData.address} onChange={handleInputChange} />
                                            <button type="submit">Save</button>
                                        </form>
                                    )}
                                </div>
                            </div>

                            <div className={styles.accordionHeader} onClick={() => setOpenPassword(p => !p)}>
                                <span>Security settings</span>
                                <span>{openPassword ? '−' : '+'}</span>
                            </div>
                            <div className={openPassword ? styles.accordionContentOpen : styles.accordionContentClosed}>
                                <div className={styles.accordionInner}>
                                    <p className={styles.settingLabel}>Email</p>
                                    <p className={styles.currentValue}>{user.email}</p>
                                    <p className={styles.changeLink} onClick={() => setEmailChange(p => !p)}>
                                        {emailChange ? 'cancel' : 'change'}
                                    </p>
                                    {emailChange && (
                                        <form onSubmit={handleEmailChange} className={styles.inlineForm}>
                                            <input type="email" name="email" placeholder="New email" value={formData.email} onChange={handleInputChange} />
                                            <button type="submit">Save</button>
                                        </form>
                                    )}

                                    <p className={styles.settingLabel} style={{ marginTop: '20px' }}>Password</p>
                                    <form onSubmit={handlePasswordChange} className={styles.inlineForm}>
                                        <input type="password" name="newPassword" placeholder="New password" value={formData.newPassword} onChange={handleInputChange} />
                                        <div className={styles.confirm}>
                                            <input type="password" name="confirmPassword" placeholder="Confirm password" value={formData.confirmPassword} onChange={handleInputChange} />
                                            {formData.confirmPassword && (
                                                <span className={styles.passwordMatch}>
                                                    {passwordMatch
                                                        ? <CiCircleCheck className={styles.match} />
                                                        : <CiCircleRemove className={styles.notMatch} />}
                                                </span>
                                            )}
                                        </div>
                                        <button type="submit">Save password</button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Login;
